/**
 * TranslatedField
 *
 * Самодостаточный компонент: показывает текст задачи на языке интерфейса
 * с кнопкой «Показать оригинал / Show translation».
 *
 * Два режима использования:
 *
 * 1. Простой (для использования ВНУТРИ .map() — хук не нужен):
 *    <TranslatedField task={task} field="title" uiLang={i18n.language} as="div" style={...} />
 *
 * 2. Контролируемый (если хотите управлять состоянием снаружи через useTranslatedTask):
 *    <TranslatedField
 *      text={translated.displayTitle}
 *      hasTranslation={translated.hasTranslation}
 *      showingOriginal={translated.showingOriginal}
 *      toggleOriginal={translated.toggleOriginal}
 *      as="div" style={...}
 *    />
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function TranslatedField({
  // Режим 1: автономный
  task,
  field,       // 'title' | 'description'
  uiLang,

  // Режим 2: контролируемый (переопределяют автономный)
  text: textProp,
  hasTranslation: hasTranslationProp,
  showingOriginal: showingOriginalProp,
  toggleOriginal: toggleOriginalProp,

  // Общие
  as: Tag = 'div',
  className,
  style,
  emptyText = null,
  onToggle,   // опц. колбэк при клике на тоггл: (showingOriginalNext) => {} — для аналитики
}) {
  const { t } = useTranslation();
  const [showOriginal, setShowOriginal] = useState(false);

  // ── Режим 1: вычисляем из task ──────────────────────────────────────────
  let text, hasTranslation, showingOriginal, toggleOriginal;

  if (task !== undefined && field !== undefined) {
    const originalLang = task?.original_lang;
    const translations = task?.translations || {};
    const lang = uiLang;

    const needsTranslation = originalLang && originalLang !== lang;
    const translation = needsTranslation ? translations[lang] : null;
    const translatedValue = translation?.[field];

    hasTranslation = !!translatedValue;
    showingOriginal = showOriginal;
    toggleOriginal = () => setShowOriginal(v => !v);

    const originalValue = task?.[field];
    text = (hasTranslation && !showOriginal) ? translatedValue : originalValue;
  } else {
    // ── Режим 2: контролируемый ─────────────────────────────────────────
    text = textProp;
    hasTranslation = hasTranslationProp;
    showingOriginal = showingOriginalProp;
    toggleOriginal = toggleOriginalProp;
  }

  if (!text && !emptyText) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Tag className={className} style={style}>
        {text || emptyText}
      </Tag>
      {hasTranslation && (
        <button
          type="button"
          onClick={e => { e.stopPropagation(); onToggle && onToggle(!showingOriginal); toggleOriginal(); }}
          style={{
            alignSelf: 'flex-start',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            fontSize: 11,
            color: 'var(--text2, #888)',
            opacity: 0.7,
            textDecoration: 'underline',
            textDecorationStyle: 'dotted',
            lineHeight: 1.6,
          }}
        >
          {showingOriginal ? t('tasks.showTranslation') : t('tasks.showOriginal')}
        </button>
      )}
    </div>
  );
}
